import { type FormEvent, useMemo, useState } from "react";
import {
  Bell,
  BookOpenText,
  Bookmark,
  ClipboardCheck,
  Link2,
  Send,
  ShieldAlert,
} from "lucide-react";
import {
  ActionPortfolio,
  CohortHeatmap,
  ContributionBars,
  DemandScatter,
  ExplainedWaterfall,
  InterventionTrend,
  AnnotatedTrend,
} from "../analytical-charts";
import { useAnalyticalContext } from "../analytical-context";
import {
  type AskAnswer,
  askAnswers,
  insufficientAnswer,
  productScopeAnswers,
} from "../ask-answers";
import { Confidence, PageHeader } from "../components";

function AnswerChart({ answer }: { answer: AskAnswer }) {
  const [selected, setSelected] = useState("Android identity verification");
  if (answer.visual === "waterfall")
    return <ExplainedWaterfall selected={selected} onSelect={setSelected} />;
  if (answer.visual === "demand") return <DemandScatter />;
  if (answer.visual === "trend") return <AnnotatedTrend signal />;
  if (answer.visual === "heatmap") return <CohortHeatmap />;
  if (answer.visual === "priority") return <ActionPortfolio />;
  if (answer.visual === "intervention") return <InterventionTrend />;
  return <ContributionBars />;
}
export function AskPage() {
  const { filters, query } = useAnalyticalContext();
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<AskAnswer>();
  const [history, setHistory] = useState<string[]>([]);
  const [notice, setNotice] = useState("");
  const match = (value: string) => {
    const matched = askAnswers.find(
      (item) => item.question.toLowerCase() === value.trim().toLowerCase(),
    );
    if (
      matched?.question === "Why did contact volume increase this period?" &&
      filters.product !== "All"
    )
      return productScopeAnswers[filters.product];
    return matched ?? insufficientAnswer;
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!question.trim()) return;
    const result = match(question);
    setAnswer(result);
    setHistory((v) => [question, ...v]);
  };
  const scope = useMemo(
    () =>
      `Last 30 days · ${filters.product === "All" ? "All products" : filters.product}`,
    [filters],
  );
  const action = (label: string) => {
    setNotice(`${label} created locally for this reproducible result.`);
    setTimeout(() => setNotice(""), 2200);
  };
  const href = `/evidence${query ? `?${query}&` : "?"}origin=${encodeURIComponent(answer?.question || "Ask Vox")}`;
  return (
    <div className="ask-page">
      <PageHeader
        eyebrow="Ask Vox · Governed natural-language investigation"
        title="Ask a business question; receive a reproducible evidence-backed answer."
        description="Answers use the active analytical scope, retain confidence and caveats, and qualify requests when evidence or permission is insufficient."
      />
      {!answer ? (
        <section className="ask-launch">
          <form onSubmit={submit}>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ask about change, demand, journeys, evidence or intervention outcomes"
              autoFocus
            />
            <button type="submit" aria-label="Ask Vox">
              <Send size={16} />
            </button>
          </form>
          <div className="ask-prompts">
            {askAnswers.map((item) => (
              <button
                type="button"
                key={item.question}
                onClick={() => {
                  const scopedAnswer = match(item.question);
                  setQuestion(item.question);
                  setAnswer(scopedAnswer);
                  setHistory((v) => [item.question, ...v]);
                }}
              >
                {item.question}
              </button>
            ))}
          </div>
          <button
            className="permission-prompt"
            type="button"
            onClick={() => {
              const item = askAnswers[3]!;
              setQuestion(item.question);
              setAnswer(item);
            }}
          >
            <ShieldAlert size={14} /> Show a permission-limited answer
          </button>
        </section>
      ) : (
        <>
          <section className="answer-panel governed-answer">
            <div className="question-recap">
              <span>Question · {scope}</span>
              <h2>{question || answer.question}</h2>
            </div>
            <div className="answer-body">
              <div className="answer-heading">
                <span>Direct conclusion</span>
                <Confidence value={answer.confidence} />
              </div>
              <p className="answer-lead">{answer.conclusion}</p>
              <ul className="answer-facts">
                {answer.quantification.map((item, index) => (
                  <li key={item}>
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <strong>{item}</strong>
                  </li>
                ))}
              </ul>
              <div className="answer-caveat">
                <BookOpenText size={16} />
                <p>
                  <strong>Confidence reason</strong> {answer.confidenceReason}
                  <br />
                  <strong>Caveat</strong> {answer.caveat}
                </p>
              </div>
              {answer.permissionLimited && (
                <div className="permission-message">
                  <ShieldAlert size={17} />
                  <p>
                    <strong>Permission-limited result.</strong> Restricted
                    records contribute only to permitted aggregates; their
                    source evidence was not returned.
                  </p>
                </div>
              )}
              <section className="ask-visual">
                <AnswerChart answer={answer} />
              </section>
              <div className="answer-sources">
                <BookOpenText size={16} />
                <span>
                  {answer.evidenceCount.toLocaleString()} evidence records ·
                  Data through 18 Jul 2026, 10:20
                </span>
                <a href={href}>View evidence</a>
              </div>
              <div className="answer-actions">
                <button type="button" onClick={() => action("Saved view")}>
                  <Bookmark size={14} /> Save view
                </button>
                <button type="button" onClick={() => action("Alert")}>
                  <Bell size={14} /> Create alert
                </button>
                <button type="button" onClick={() => action("Investigation")}>
                  <ClipboardCheck size={14} /> Start investigation
                </button>
                <button type="button" onClick={() => action("Share link")}>
                  <Link2 size={14} /> Share
                </button>
              </div>
            </div>
            <form className="ask-input ask-input--followup" onSubmit={submit}>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Ask a follow-up in the same scope"
              />
              <button type="submit">
                <Send size={15} />
              </button>
            </form>
          </section>
          <button
            className="text-link"
            type="button"
            onClick={() => {
              setAnswer(undefined);
              setQuestion("");
            }}
          >
            Start a new investigation
          </button>
        </>
      )}
      {notice && (
        <div className="demo-toast" role="status">
          {notice}
        </div>
      )}
      {history.length > 0 && (
        <aside className="ask-history">
          <strong>Investigation history</strong>
          {history.slice(0, 4).map((x, i) => (
            <span key={`${x}-${i}`}>{x}</span>
          ))}
        </aside>
      )}
    </div>
  );
}
